/**
 * Typography Components
 * Education-focused typography system for Vidhyantra SMS
 * Uses Poppins/Nunito fonts with friendly, readable styling
 */

import * as React from 'react'
import { ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  children?: ReactNode
  className?: string
  as?: keyof JSX.IntrinsicElements
}

/**
 * Display Text (3.5rem / 56px) - Larger for friendly education theme
 */
export function Display({ children, className, as: Component = 'h1' }: TypographyProps) {
  return (
    <Component
      className={cn(
        'text-[3.5rem] font-bold leading-tight tracking-tight',
        className
      )}
    >
      {children}
    </Component>
  )
}

/**
 * Heading 1 (2.5rem / 40px)
 */
export function H1({ children, className, as: Component = 'h1' }: TypographyProps) {
  return (
    <Component
      className={cn(
        'text-[100%] font-bold leading-tight tracking-tight',
        className
      )}
    >
      {children}
    </Component>
  )
}

/**
 * Heading 2 (2rem / 32px)
 */
export function H2({ children, className, as: Component = 'h2' }: TypographyProps) {
  return (
    <Component
      className={cn(
        'text-[100%] font-semibold leading-tight tracking-tight',
        className
      )}
    >
      {children}
    </Component>
  )
}

/**
 * Heading 3 (1.75rem / 28px)
 */
export const H3 = React.forwardRef<HTMLHeadingElement, TypographyProps>(
  ({ children, className, as: Component = 'h3', ...props }, ref) => {
    const Comp = Component as any
    return (
      <Comp
        ref={ref}
        className={cn(
          'text-[100%] font-semibold leading-snug tracking-tight',
          className
        )}
        {...props}
      >
        {children}
      </Comp>
    )
  }
)
H3.displayName = 'H3'

/**
 * Heading 4 (1.5rem / 24px)
 */
export function H4({ children, className, as: Component = 'h4' }: TypographyProps) {
  return (
    <Component
      className={cn(
        'text-[100%] font-semibold leading-snug',
        className
      )}
    >
      {children}
    </Component>
  )
}

/**
 * Heading 5 (1.25rem / 20px)
 */
export function H5({ children, className, as: Component = 'h5' }: TypographyProps) {
  return (
    <Component
      className={cn(
        'text-[100%] font-medium leading-snug',
        className
      )}
    >
      {children}
    </Component>
  )
}

/**
 * Heading 6 (1.125rem / 18px) - Slightly larger for readability
 */
export function H6({ children, className, as: Component = 'h6' }: TypographyProps) {
  return (
    <Component
      className={cn(
        'text-[100%] font-medium leading-snug',
        className
      )}
    >
      {children}
    </Component>
  )
}

/**
 * Body Text (1rem / 16px)
 */
export function Body({ children, className, as: Component = 'p' }: TypographyProps) {
  return (
    <Component
      className={cn(
        'text-[100%] leading-relaxed',
        className
      )}
    >
      {children}
    </Component>
  )
}

/**
 * Small Text (0.875rem / 14px)
 */
export const Small = React.forwardRef<HTMLElement, TypographyProps>(
  ({ children, className, as: Component = 'span', ...props }, ref) => {
    const Comp = Component as any
    return (
      <Comp
        ref={ref}
        className={cn(
          'text-sm leading-relaxed',
          className
        )}
        {...props}
      >
        {children}
      </Comp>
    )
  }
)
Small.displayName = 'Small'

/**
 * Caption Text (0.75rem / 12px)
 */
export function Caption({ children, className, as: Component = 'span' }: TypographyProps) {
  return (
    <Component
      className={cn(
        'text-xs leading-relaxed',
        className
      )}
    >
      {children}
    </Component>
  )
}

/**
 * Label Text (for form labels)
 */
export function Label({ children, className, as: Component = 'label' }: TypographyProps) {
  return (
    <Component
      className={cn(
        'text-sm font-medium leading-none',
        className
      )}
    >
      {children}
    </Component>
  )
}

export const Typography = {
  Display,
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
  Body,
  Small,
  Caption,
  Label,
}

